import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Container,
  IconButton,
  Snackbar,
} from "@mui/material";
import {
  DarkMode,
  LightMode,
  Send as SendIcon,
  Login as LoginIcon
} from "@mui/icons-material";
import { useTheme } from "../theme/ThemeProvider";
import { sendOtp, verifyOtp } from "../services/authService";
import { useAuth } from "../contexts/AuthContext";

const logoPath = `${process.env.PUBLIC_URL}/bitnet-logo-high-res.png`;

const OTPLogin = () => {
  const [email, setEmail] = useState("support@bitnetit.in");
  const [otp, setOtp] = useState("");
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [otpError, setOtpError] = useState("");

  const navigate = useNavigate();
  const { isDarkMode, toggleTheme } = useTheme();
  const { otpLogin } = useAuth();

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle Send OTP
  const handleSendOtp = async () => {
    setError("");
    setEmailError("");
    setSuccessMessage("");

    // Validate email
    if (!email || !validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    // Check if email is from BitNet organization
    if (!email.toLowerCase().includes("@bitnetit.in")) {
      setEmailError("Only BitNet organization emails are allowed");
      return;
    }

    setLoading(true);

    try {
      const response = await sendOtp(email);

      if (response.success) {
        setSuccessMessage("OTP sent successfully! Check your email.");
        setShowOtpInput(true);
        setSnackbarOpen(true);
      } else {
        setError(response.message || "Failed to send OTP. Please try again.");
      }
    } catch (err) {
      console.error("Send OTP error:", err);
      setError(err.message || "Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle Login with OTP
  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setOtpError("");

    // Validate OTP
    if (!otp || otp.length !== 6) {
      setOtpError("Please enter a valid 6-digit OTP");
      return;
    }

    setLoading(true);

    try {
      const response = await verifyOtp(email, otp);

      if (response.success && response.token) {
        // Decode token to get user info
        const base64Url = response.token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split('')
            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        );
        const decoded = JSON.parse(jsonPayload);

        // Verify user has BitNetOrgAdmin role
        if (decoded.role === "BitNetOrgAdmin") {
          // Update AuthContext state (stores token + user in localStorage and context)
          await otpLogin(response.token, {
            id: decoded.userId,
            email: decoded.email,
            role: decoded.role,
            companyName: decoded.companyName,
            name: decoded.name,
          });

          setSuccessMessage("Login successful! Redirecting to Client Management...");
          setSnackbarOpen(true);

          // Redirect to /clients after a short delay
          setTimeout(() => {
            navigate("/clients", { replace: true });
          }, 1000);
        } else {
          setError("Access denied. This login is only for BitNet organization administrators.");
          localStorage.removeItem("token");
        }
      } else {
        setOtpError(response.message || "Invalid OTP. Please try again.");
      }
    } catch (err) {
      console.error("OTP verification error:", err);
      setOtpError(err.message || "Invalid OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f3f2f1', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          py: 4,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 450,
            textAlign: "center",
            overflow: 'hidden',
            background: '#ffffff',
            borderRadius: 3,
            border: '1px solid #edebe9',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          {/* Azure-style navy accent strip */}
          <Box sx={{ height: 4, backgroundColor: '#0c2d48', width: '100%' }} />
          <Box sx={{ p: 5 }}>
          {/* Logo */}
          <Box sx={{ mb: 3 }}>
            <img
              src={logoPath}
              alt="BitNet Logo"
              style={{
                maxWidth: 80,
                height: 'auto',
                filter: isDarkMode ? 'brightness(0.9)' : 'none'
              }}
            />
          </Box>

          {/* Title */}
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              fontWeight: 700,
              mb: 1,
              color: '#201f1e',
            }}
          >
            Organization Admin
          </Typography>

          <Typography
            variant="body1"
            sx={{
              mb: 4,
              color: 'text.secondary',
              fontSize: '1rem'
            }}
          >
            Secure OTP-based login for BitNet administrators
          </Typography>

          {/* OTP Login Form */}
          <Box component="form" onSubmit={handleLogin} sx={{ mt: 2 }}>
            {/* Email Input */}
            <TextField
              fullWidth
              label="BitNet Organization Email"
              margin="normal"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading || showOtpInput}
              error={!!emailError}
              helperText={emailError || "Enter your @bitnetit.in email"}
              sx={{ mb: 2 }}
            />

            {/* Send OTP Button */}
            {!showOtpInput && (
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleSendOtp}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                sx={{
                  mb: 3,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                }}
              >
                {loading ? "Sending OTP..." : "Send OTP"}
              </Button>
            )}

            {/* OTP Input (shown after Send OTP) */}
            {showOtpInput && (
              <>
                <TextField
                  fullWidth
                  label="Enter 6-Digit OTP"
                  margin="normal"
                  required
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  disabled={loading}
                  error={!!otpError}
                  helperText={otpError || "Check your email for the OTP"}
                  inputProps={{
                    maxLength: 6,
                    pattern: "[0-9]*",
                    inputMode: "numeric"
                  }}
                  sx={{ mb: 3 }}
                />

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  type="submit"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <LoginIcon />}
                  sx={{
                    mb: 2,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                  }}
                >
                  {loading ? "Verifying..." : "Login"}
                </Button>

                {/* Resend OTP Button */}
                <Button
                  fullWidth
                  variant="text"
                  size="medium"
                  onClick={handleSendOtp}
                  disabled={loading}
                  sx={{ mb: 3 }}
                >
                  Resend OTP
                </Button>
              </>
            )}

            {/* Success Message */}
            {successMessage && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {successMessage}
              </Alert>
            )}

            {/* Error Messages */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}
          </Box>{/* /form */}
          </Box>{/* /p:5 */}
        </Paper>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          message={successMessage}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </Box>
    </Container>
    </Box>
  );
};

export default OTPLogin;
